import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { prettyDate, prettyDateShort, buildFoalCardHtml, buildFoalRegistryHtml, printHtml, foalName, DEFAULT_SETTINGS } from "@/lib/equi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, Loader2, ClipboardList, Printer, Search } from "lucide-react";

const EDIT_FIELDS = [
  { key: "foal_out_date", label: "Foal Out Date", type: "date" },
  { key: "stallion", label: "Stallion", type: "text" },
  { key: "sex", label: "Sex", type: "text" },
  { key: "color", label: "Color", type: "text" },
  { key: "breed", label: "Breed", type: "text" },
];

export default function Foals() {
  const { readOnly } = useAuth();
  const [foals, setFoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [recordsFoal, setRecordsFoal] = useState(null);
  const [recordsData, setRecordsData] = useState(null);
  const [recordsLoading, setRecordsLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [printPrompt, setPrintPrompt] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [f, s] = await Promise.all([api.get("/foals"), api.get("/settings")]);
      setFoals(f.data);
      setSettings(s.data);
    } catch {
      toast.error("Could not load foals");
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const saveEdit = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      await api.put(`/foals/${editing.id}`, {
        foal_out_date: editing.foal_out_date || "",
        stallion: editing.stallion || "",
        sex: editing.sex || "",
        color: editing.color || "",
        breed: editing.breed || "",
        foal_out_notes: editing.foal_out_notes || "",
        deceased: !!editing.deceased,
        igg_result: editing.igg_result || "",
        igg_drawn_at: editing.igg_drawn_at || "",
        igg_recheck_result: editing.igg_recheck_result || "",
        igg_recheck_drawn_at: editing.igg_recheck_drawn_at || "",
      });
      toast.success("Foal updated");
      setEditing(null);
      load(true);
    } catch {
      toast.error("Failed to update foal");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/foals/${deleteTarget.id}`);
      toast.success("Foal removed");
      setDeleteTarget(null);
      load(true);
    } catch {
      toast.error("Failed to remove foal");
    }
  };

  const openRecords = async (foal) => {
    setRecordsFoal(foal);
    setRecordsData(null);
    setRecordsLoading(true);
    try {
      const { data } = await api.get(`/foals/${foal.id}/records`);
      setRecordsData(data);
    } catch {
      toast.error("Could not load foal records");
    } finally {
      setRecordsLoading(false);
    }
  };

  const printFoalCard = () => {
    if (!recordsData) return;
    printHtml(buildFoalCardHtml(recordsData.foal, recordsData.records, recordsData.farrier || [], settings));
  };

  const printRegistry = () => {
    if (!foals.length) {
      toast.error("No foals to print yet");
      return;
    }
    if (foals.some((f) => f.deceased)) {
      setPrintPrompt(true);
      return;
    }
    printHtml(buildFoalRegistryHtml(foals, settings, true));
  };

  const doPrintRegistry = (includeDeceased) => {
    setPrintPrompt(false);
    printHtml(buildFoalRegistryHtml(foals, settings, includeDeceased));
  };

  const cols = ["Mare", "Foal Out Date", "Stallion", "Sex", "Color", "Breed", "Farm"];

  const q = search.trim().toLowerCase();
  const filtered = q
    ? foals.filter(
        (f) =>
          (f.mare_name || "").toLowerCase().includes(q) ||
          (f.stallion || "").toLowerCase().includes(q) ||
          (f.sex || "").toLowerCase().includes(q) ||
          (f.color || "").toLowerCase().includes(q) ||
          (f.breed || "").toLowerCase().includes(q)
      )
    : foals;

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight">Foal Registry</h1>
          <p className="text-muted-foreground text-sm">Foals are created automatically when you set a Foal Out Date on a breeding card.</p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search foals by mare, stallion, sex…"
              className="pl-9"
              data-testid="foals-search-input"
            />
          </div>
          <Button onClick={printRegistry} className="gap-2 shrink-0" data-testid="foals-print-registry-btn">
            <Printer className="h-4 w-4" /> Print Registry
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : foals.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl">
          <p className="text-lg font-semibold">No foals yet</p>
          <p className="text-muted-foreground text-sm mt-1">Open a mare&apos;s Breeding Card and set a Foal Out Date to register her foal.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-card border border-dashed border-border rounded-xl" data-testid="foals-no-matches">
          <p className="text-lg font-semibold">No foals match “{search.trim()}”</p>
          <p className="text-muted-foreground text-sm mt-1">Try a different mare, stallion, or sex.</p>
        </div>
      ) : (
        <div className="border border-[#C4C8D6] rounded-md bg-card overflow-x-auto equi-scroll">
          <table className="w-full text-[13px] min-w-[820px]">
            <thead>
              <tr className="bg-primary text-primary-foreground text-[11px] uppercase tracking-wide [&>th]:border-r [&>th]:border-white/15 last:[&>th]:border-r-0">
                {cols.map((c) => (
                  <th key={c} className="px-4 py-3 text-left font-semibold whitespace-nowrap">{c}</th>
                ))}
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((f) => (
                <tr key={f.id} className="border-b border-[#C4C8D6] hover:bg-primary/5 [&>td]:border-r [&>td]:border-[#C4C8D6] last:[&>td]:border-r-0" data-testid={`foal-row-${f.id}`}>
                  <td className="px-4 py-3 font-semibold whitespace-nowrap">
                    {f.mare_name || "—"}
                    {f.deceased && (
                      <span className="ml-2 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide bg-muted text-muted-foreground border border-border" data-testid={`foal-deceased-badge-${f.id}`}>
                        Deceased
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">{f.foal_out_date ? prettyDate(f.foal_out_date) : "—"}</td>
                  <td className="px-4 py-3">{f.stallion || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3">{f.sex || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3">{f.color || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3">{f.breed || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3 text-muted-foreground">{f.farm || <span className="text-border">—</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="gap-1.5" onClick={() => openRecords(f)} data-testid={`foal-records-${f.id}`}>
                        <ClipboardList className="h-4 w-4" /> Records
                      </Button>
                      {!readOnly && (
                        <>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => setEditing({ ...f })} data-testid={`foal-edit-${f.id}`}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive" onClick={() => setDeleteTarget(f)} data-testid={`foal-delete-${f.id}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit foal · {editing?.mare_name}</DialogTitle>
          </DialogHeader>
          {editing && (
            <>
              <div className="grid grid-cols-2 gap-4 py-2">
                {EDIT_FIELDS.map((f) => (
                  <div key={f.key} className="space-y-1.5">
                    <Label htmlFor={`fe-${f.key}`}>{f.label}</Label>
                    <Input
                      id={`fe-${f.key}`}
                      type={f.type}
                      value={editing[f.key] || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, [f.key]: e.target.value }))}
                      data-testid={`foal-editfield-${f.key}`}
                    />
                  </div>
                ))}
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fe-foal_out_notes">Foal Out Notes</Label>
                <Textarea
                  id="fe-foal_out_notes"
                  rows={3}
                  value={editing.foal_out_notes || ""}
                  placeholder="Notes from foaling out (presentation, complications, observations…)"
                  onChange={(e) => setEditing((s) => ({ ...s, foal_out_notes: e.target.value }))}
                  data-testid="foal-editfield-foal_out_notes"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border border-border px-3 py-2 mt-3">
                <div>
                  <p className="text-sm font-semibold">Status</p>
                  <p className="text-xs text-muted-foreground">{editing.deceased ? "Marked as deceased" : "Active"}</p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className={editing.deceased ? "text-emerald-600" : "text-destructive"}
                  onClick={() => setEditing((s) => ({ ...s, deceased: !s.deceased }))}
                  data-testid="foal-deceased-toggle"
                >
                  {editing.deceased ? "Mark Active" : "Mark Deceased"}
                </Button>
              </div>
              <div className="mt-1 pt-3 border-t border-border">
                <h4 className="font-bold text-xs uppercase tracking-wide text-primary mb-2">Immunoglobulin (IgG)</h4>
                <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">IgG Result</p>
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="fe-igg_result">Result</Label>
                    <Input
                      id="fe-igg_result"
                      value={editing.igg_result || ""}
                      placeholder="e.g. > 800 mg/dL"
                      onChange={(e) => setEditing((s) => ({ ...s, igg_result: e.target.value }))}
                      data-testid="foal-editfield-igg_result"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fe-igg_drawn_at">Date / Time of Draw</Label>
                    <Input
                      id="fe-igg_drawn_at"
                      type="datetime-local"
                      value={editing.igg_drawn_at || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, igg_drawn_at: e.target.value }))}
                      data-testid="foal-editfield-igg_drawn_at"
                    />
                  </div>
                </div>
                <p className="text-[11px] font-semibold text-muted-foreground mb-1.5">Recheck IgG</p>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="fe-igg_recheck_result">Result</Label>
                    <Input
                      id="fe-igg_recheck_result"
                      value={editing.igg_recheck_result || ""}
                      placeholder="e.g. > 800 mg/dL"
                      onChange={(e) => setEditing((s) => ({ ...s, igg_recheck_result: e.target.value }))}
                      data-testid="foal-editfield-igg_recheck_result"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fe-igg_recheck_drawn_at">Date / Time of Draw</Label>
                    <Input
                      id="fe-igg_recheck_drawn_at"
                      type="datetime-local"
                      value={editing.igg_recheck_drawn_at || ""}
                      onChange={(e) => setEditing((s) => ({ ...s, igg_recheck_drawn_at: e.target.value }))}
                      data-testid="foal-editfield-igg_recheck_drawn_at"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="foal-save-btn">
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this foal record?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {deleteTarget?.mare_name}&apos;s foal record (foal out {deleteTarget?.foal_out_date}). This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="foal-delete-confirm">
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={printPrompt} onOpenChange={setPrintPrompt}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Include deceased foals?</AlertDialogTitle>
            <AlertDialogDescription>
              Some foals are marked deceased. Choose whether to include them in the printed registry. Included deceased foals are labeled &ldquo;(Deceased)&rdquo; next to the name.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="foal-print-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => doPrintRegistry(false)} className="bg-secondary text-secondary-foreground hover:bg-secondary/80" data-testid="foal-print-exclude">
              Active only
            </AlertDialogAction>
            <AlertDialogAction onClick={() => doPrintRegistry(true)} data-testid="foal-print-include">
              Include deceased
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>


      {/* Foal exam records (DESC on screen; Print Foal Card prints ASC) */}
      <Dialog open={!!recordsFoal} onOpenChange={(o) => { if (!o) { setRecordsFoal(null); setRecordsData(null); } }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{recordsFoal ? `${foalName(recordsFoal)} · exam records` : ""}</DialogTitle>
          </DialogHeader>
          {recordsLoading ? (
            <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {recordsData?.total || 0} exam{(recordsData?.total || 0) === 1 ? "" : "s"} · {recordsData?.filled || 0} with entries
                </p>
                <Button onClick={printFoalCard} disabled={!recordsData} className="gap-1.5" data-testid="foal-print-card-btn">
                  <Printer className="h-4 w-4" /> Print Foal Card
                </Button>
              </div>
              {(!recordsData || recordsData.records.length === 0) ? (
                <p className="text-sm text-muted-foreground py-8 text-center">
                  No exam records yet. Add this foal to Daily Rounds to begin.
                </p>
              ) : (
                <div className="border border-border rounded-lg divide-y divide-border mt-2">
                  {recordsData.records.map((r) => (
                    <div key={r.id} className="px-3 py-2.5 text-sm" data-testid={`foal-rec-${r.id}`}>
                      <div className="font-semibold">{prettyDate(r.date)}</div>
                      <div className="text-muted-foreground text-xs mt-0.5">
                        Exam: {r.physical_exam || "—"} · Umbilicus: {r.umbilicus || "—"} · Meds: {r.medication || "—"} · Bandages: {r.bandages || "—"}
                        {r.next_check ? ` · Next: ${prettyDateShort(r.next_check)}` : ""}
                      </div>
                      {r.notes && <div className="italic text-muted-foreground text-xs mt-0.5">Notes: {r.notes}</div>}
                      {r.additional_procedures && <div className="italic text-muted-foreground text-xs mt-0.5">Procedures: {r.additional_procedures}</div>}
                    </div>
                  ))}
                </div>
              )}
              {recordsData?.farrier?.length > 0 && (
                <div className="mt-4" data-testid="foal-farrier-section">
                  <h4 className="font-bold text-xs uppercase tracking-wide text-primary mb-2">Farrier Record</h4>
                  <div className="border border-border rounded-lg divide-y divide-border">
                    {recordsData.farrier.map((r) => (
                      <div key={r.id} className="px-3 py-2.5 text-sm" data-testid={`foal-farrier-rec-${r.id}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">{prettyDate(r.date)}</span>
                          {r.next_due && <span className="text-xs text-primary font-semibold">Next due {prettyDateShort(r.next_due)}</span>}
                        </div>
                        {r.notes && <div className="italic text-muted-foreground text-xs mt-0.5">{r.notes}</div>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
